import { AuthApi, Configuration } from '@sr-sdks/api-sdk-axios';
import {
  confirmUserAttribute,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  sendUserAttributeVerificationCode,
  updateMFAPreference,
  updateUserAttributes,
} from 'aws-amplify/auth';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { configuration } from '../../../../configuration';
import { ApiList } from '../../../../utils/api/ApiList';
import { safeRedirect } from '../../../../utils/helpers/common';
import { AppLoader } from '../../../Loader';
import { managedLoginSignOut } from '../CognitoManagedLoginProvider';
import {
  clearDestinationWrittenMarker,
  clearFallbackDestinations,
  takeParkedDestination,
} from '../postAuthRedirect';
import CodeInput from './CodeInput';
import MfaSetupScreen from './MfaSetupScreen';
import { SetupContainer } from './SetupContainer';

const plainAxios = axios.create();
const authApi = new AuthApi(
  new Configuration(),
  ApiList.permissions || '',
  plainAxios,
);

type GateState =
  'email_verifying' | 'loading' | 'ready' | 'setup_required' | 'sms_confirming';

type MfaSetupGateProps = React.PropsWithChildren;

const maskPhone = (phone: string): string => {
  if (phone.length <= 6) return phone;

  return (
    phone.slice(0, 3) + '\u2022'.repeat(phone.length - 6) + phone.slice(-3)
  );
};

const clearAuth0MfaType = async (): Promise<void> => {
  try {
    await updateUserAttributes({
      userAttributes: { 'custom:auth0_mfa_type': '' },
    });
  } catch {
    // Best-effort cleanup; MFA is already enabled so this is non-critical
  }
};

const MfaSetupGate = ({ children }: MfaSetupGateProps) => {
  const [state, setState] = useState<GateState>('loading');
  const [userEmail, setUserEmail] = useState<string | undefined>();
  const [migratedPhone, setMigratedPhone] = useState<string>('');
  const [smsCode, setSmsCode] = useState('');
  const [smsError, setSmsError] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [handingBack, setHandingBack] = useState(false);
  const codeSentRef = useRef(false);
  const emailCodeSentRef = useRef(false);
  const [emailCode, setEmailCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResendCountdown, setEmailResendCountdown] = useState(0);

  const checkMfa = useCallback(async () => {
    try {
      const session = await fetchAuthSession();

      const identities = session.tokens?.idToken?.payload?.identities as
        undefined | unknown[];

      if (identities?.length) {
        setState('ready');

        return;
      }

      const mfaClaim =
        session.tokens?.accessToken?.payload?.['custom:mfa_configured'];

      if (mfaClaim === 'true') {
        setState('ready');

        return;
      }

      const [prefs, attrs] = await Promise.all([
        fetchMFAPreference().catch(() => ({
          enabled: undefined,
          preferred: undefined,
        })),
        fetchUserAttributes().catch(() => null),
      ]);

      if (attrs?.email) setUserEmail(attrs.email);

      const hasTotp = prefs.enabled?.includes('TOTP');
      const hasSms = prefs.enabled?.includes('SMS');
      const hasMfa = hasTotp || hasSms || !!prefs.preferred;

      if (hasMfa) {
        setState('ready');

        return;
      }

      const emailVerified = attrs?.email_verified;

      if (attrs && emailVerified !== 'true') {
        setState('email_verifying');

        return;
      }

      const auth0MfaType =
        attrs?.['custom:auth0_mfa_type' as keyof typeof attrs];
      const phoneNumber = attrs?.phone_number;

      if (auth0MfaType === 'sms' && phoneNumber) {
        setMigratedPhone(phoneNumber);
        setState('sms_confirming');

        return;
      }

      setState('setup_required');
    } catch {
      setState('setup_required');
    }
  }, []);

  useEffect(() => {
    checkMfa();
  }, [checkMfa]);

  /**
   * Hand the member back to the app they came from, recovered from the OAuth
   * `state` round-trip (ADR-CM-037).
   *
   * Drained *here*, at the point the gate lets children through, rather than
   * in the bootstrap Hub listener. Redirecting straight from the listener
   * would jump the migration cohort back to MSR2 before MFA setup ran; MSR2's
   * 401 interceptor would then bounce them to `/auth/mfa-setup?return_to=`
   * and they would come back again -- two extra round trips for exactly the
   * members least able to absorb them.
   *
   * Only the `external` kind is taken: an internal path belongs to
   * `LoginCallback`, which sits inside the router and can `navigate()` to it
   * rather than reloading the whole app.
   */
  useEffect(() => {
    if (state !== 'ready') return;
    const destination = takeParkedDestination('external');

    if (!destination) return;
    // Terminal branch for this round trip -- drop the marker so a later
    // sign-in in the same tab cannot report this one as "recorded then lost".
    clearDestinationWrittenMarker();
    // And drop the fallback keys this round trip wrote. `LoginCallback` is
    // the only other place that clears them and it cannot run here: it is a
    // lazy route, so on the cold load after the OAuth redirect its chunk is
    // still in flight when this effect redirects away. Left behind, the keys
    // survive in this tab and hijack the member's *next* sign-in.
    clearFallbackDestinations();
    setHandingBack(true);
    window.location.href = destination.value;
  }, [state]);

  useEffect(() => {
    if (state !== 'sms_confirming' || codeSentRef.current) return;
    codeSentRef.current = true;

    sendUserAttributeVerificationCode({
      userAttributeKey: 'phone_number',
    })
      .then(() => setResendCountdown(30))
      .catch((err) => {
        setSmsError(`Failed to send SMS code: ${err.message}`);
      });
  }, [state]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);

    return () => clearTimeout(timer);
  }, [resendCountdown]);

  // Auto-send email verification code when entering the email_verifying state
  useEffect(() => {
    if (state !== 'email_verifying' || emailCodeSentRef.current || !userEmail)
      return;
    emailCodeSentRef.current = true;

    authApi
      .authControllerRequestEmailVerification({
        email: userEmail.trim().toLowerCase(),
      })
      .then(() => setEmailResendCountdown(30))
      .catch((err) => {
        setEmailError(
          err?.response?.data?.message ||
            'Failed to send verification code. Please try again.',
        );
      });
  }, [state, userEmail]);

  useEffect(() => {
    if (emailResendCountdown <= 0) return;
    const timer = setTimeout(() => setEmailResendCountdown((c) => c - 1), 1000);

    return () => clearTimeout(timer);
  }, [emailResendCountdown]);

  const handleEmailVerify = async () => {
    if (!userEmail) {
      setEmailError('Unable to verify email. Please sign in again.');

      return;
    }

    if (emailCode.length !== 6) {
      setEmailError('Please enter the 6-digit code');

      return;
    }

    setEmailLoading(true);
    setEmailError('');

    try {
      await authApi.authControllerConfirmEmailVerification({
        code: emailCode.trim(),
        email: userEmail.trim().toLowerCase(),
      });
      await checkMfa();
    } catch (err: any) {
      setEmailError(
        err?.response?.data?.message ||
          'Invalid or expired code. Please try again.',
      );
      setEmailLoading(false);
    }
  };

  const handleEmailResend = async () => {
    if (!userEmail) {
      setEmailError('Unable to resend code. Please sign in again.');

      return;
    }

    setEmailError('');
    try {
      await authApi.authControllerRequestEmailVerification({
        email: userEmail.trim().toLowerCase(),
      });
      setEmailResendCountdown(30);
    } catch (err: any) {
      setEmailError(err?.response?.data?.message || 'Failed to resend code.');
    }
  };

  const handleSmsVerify = async () => {
    if (smsCode.length !== 6) {
      setSmsError('Please enter the 6-digit code');

      return;
    }

    setSmsLoading(true);
    setSmsError('');

    try {
      await confirmUserAttribute({
        confirmationCode: smsCode,
        userAttributeKey: 'phone_number',
      });
      await updateMFAPreference({ sms: 'PREFERRED' });
      await clearAuth0MfaType();
      await fetchAuthSession({ forceRefresh: true });
      setState('ready');
    } catch (err: any) {
      setSmsError(err.message || 'Invalid code. Please try again.');
      setSmsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setSmsError('');
    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number',
      });
      setResendCountdown(30);
    } catch (err: any) {
      setSmsError(`Failed to resend code: ${err.message}`);
    }
  };

  if (state === 'loading' || handingBack) {
    return <AppLoader.FlexLoader loaderHeight="h-screen" />;
  }

  if (state === 'sms_confirming') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100">
        <SetupContainer>
          <div className="p-6 text-center">
            <h4 className="text-lg font-bold">Enter verification code</h4>
            <p className="pb-4 pt-2 text-sm text-sr-text-secondary">
              Check your phone. We have texted a code to{' '}
              <strong>{maskPhone(migratedPhone)}</strong>. It may take a minute
              to arrive.
            </p>

            {smsError && (
              <div className="mb-3 rounded-md bg-sr-input-error p-3">
                <p className="text-sm text-sr-dark-red">{smsError}</p>
              </div>
            )}

            <div className="text-left">
              <CodeInput
                isLoading={smsLoading}
                value={smsCode}
                onChange={(val) => {
                  setSmsCode(val);
                  setSmsError('');
                }}
                onSubmit={handleSmsVerify}
              />
            </div>

            <button
              className="mt-6 w-full rounded-md bg-sr-text-brand px-4 py-3 text-sm font-medium text-white hover:bg-sr-text-brand-hover active:bg-sr-text-brand-active disabled:cursor-not-allowed disabled:opacity-50"
              disabled={smsLoading || smsCode.length !== 6}
              type="button"
              onClick={handleSmsVerify}
            >
              {smsLoading ? 'Verifying...' : 'Verify'}
            </button>

            <button
              className="mt-4 w-full rounded-md border border-sr-text-brand bg-transparent px-4 py-3 text-sm font-medium text-sr-text-brand hover:border-sr-dark-red hover:text-sr-dark-red disabled:cursor-not-allowed disabled:border-sr-outline disabled:text-sr-light-gray"
              disabled={resendCountdown > 0}
              type="button"
              onClick={handleResendCode}
            >
              {resendCountdown > 0
                ? `Resend Code (${resendCountdown}s)`
                : 'Resend Code'}
            </button>

            <button
              className="mt-6 w-full bg-transparent text-sm font-medium text-sr-text-brand hover:text-sr-text-brand-hover"
              type="button"
              onClick={() => {
                setSmsCode('');
                setSmsError('');
                setResendCountdown(0);
                setState('setup_required');
              }}
            >
              I lost my authenticator
            </button>

            <button
              className="mt-5 w-full bg-transparent text-sm font-medium text-sr-text-brand hover:text-sr-text-brand-hover"
              type="button"
              onClick={() => managedLoginSignOut()}
            >
              Cancel
            </button>
          </div>
        </SetupContainer>
      </div>
    );
  }

  if (state === 'email_verifying') {
    const maskedEmail = userEmail
      ? userEmail.replace(
          /(.{2})(.*)(@.*)/,
          (_, a, b, c) => a + '*'.repeat(b.length) + c,
        )
      : '';

    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100">
        <SetupContainer>
          <div className="p-6 text-center">
            <h4 className="text-lg font-bold">Verify your email</h4>
            <p className="pb-4 pt-2 text-sm text-sr-text-secondary">
              We have sent a verification code to <strong>{maskedEmail}</strong>
              . Please enter the code below to continue.
            </p>

            {emailError && (
              <div className="mb-3 rounded-md bg-sr-input-error p-3">
                <p className="text-sm text-sr-dark-red">{emailError}</p>
              </div>
            )}

            <div className="text-left">
              <CodeInput
                isLoading={emailLoading}
                value={emailCode}
                onChange={(val) => {
                  setEmailCode(val);
                  setEmailError('');
                }}
                onSubmit={handleEmailVerify}
              />
            </div>

            <button
              className="mt-6 w-full rounded-md bg-sr-text-brand px-4 py-3 text-sm font-medium text-white hover:bg-sr-text-brand-hover active:bg-sr-text-brand-active disabled:cursor-not-allowed disabled:opacity-50"
              disabled={emailLoading || emailCode.length !== 6}
              type="button"
              onClick={handleEmailVerify}
            >
              {emailLoading ? 'Verifying...' : 'Verify'}
            </button>

            <button
              className="mt-4 w-full rounded-md border border-sr-text-brand bg-transparent px-4 py-3 text-sm font-medium text-sr-text-brand hover:border-sr-dark-red hover:text-sr-dark-red disabled:cursor-not-allowed disabled:border-sr-outline disabled:text-sr-light-gray"
              disabled={emailResendCountdown > 0}
              type="button"
              onClick={handleEmailResend}
            >
              {emailResendCountdown > 0
                ? `Resend Code (${emailResendCountdown}s)`
                : 'Resend Code'}
            </button>

            <button
              className="mt-5 w-full bg-transparent text-sm font-medium text-sr-text-brand hover:text-sr-text-brand-hover"
              type="button"
              onClick={() => managedLoginSignOut()}
            >
              Cancel
            </button>
          </div>
        </SetupContainer>
      </div>
    );
  }

  if (state === 'setup_required') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-100">
        <MfaSetupScreen
          userEmail={userEmail}
          onCancel={() => managedLoginSignOut()}
          onComplete={async () => {
            await fetchAuthSession({ forceRefresh: true });
            const returnTo = safeRedirect(
              new URLSearchParams(window.location.search).get('return_to'),
              '/',
              [configuration.MYSCREENRIGHTS_BASE],
            );

            if (returnTo !== '/') {
              window.location.href = returnTo;

              return;
            }

            // First-password flow skips /login-callback, so route via the
            // pendingInvitation localStorage entry instead.
            const pendingInvitationData =
              localStorage.getItem('pendingInvitation');

            if (pendingInvitationData) {
              try {
                const data = JSON.parse(pendingInvitationData);

                if (data?.workspaceId && data?.token) {
                  localStorage.removeItem('pendingInvitation');
                  window.location.href = `/onboarding/${data.workspaceId}/create-profile?code=${data.token}`;

                  return;
                }
              } catch {
                localStorage.removeItem('pendingInvitation');
              }
            }

            setState('ready');
          }}
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default MfaSetupGate;
